import { UserInfo } from "../types/common";
import {
  memberSessionStorage,
  isLegacyMemberToken,
} from "./memberSessionStorage";

const tokenKey = "authToken";
const refreshTokenKey = "refreshToken";
const userInfoKey = "userInfo";
const trustedDeviceTokenKey = "staff_trusted_device_token";
// Non-secret login nonce shared across tabs because the staff refresh Cookie is shared.
export const staffSessionRevisionKey = "staffSessionRevision.v1";
const staffTabRevisionKey = "staffTabRevision.v1";

let inMemoryAccessToken: string | null = null;

const getLocalStorage = (): Storage | null => {
  if (typeof window === "undefined") {
    return null;
  }

  return window.localStorage;
};

const getSessionStorage = (): Storage | null => {
  if (typeof window === "undefined") {
    return null;
  }

  return window.sessionStorage;
};

const migrateLegacyTokenIfNeeded = (): void => {
  const sessionStorage = getSessionStorage();
  const localStorage = getLocalStorage();

  if (!sessionStorage || !localStorage) {
    return;
  }

  const sessionToken = sessionStorage.getItem(tokenKey);
  if (sessionToken) {
    inMemoryAccessToken = sessionToken;
    return;
  }

  const legacyToken = localStorage.getItem(tokenKey);
  if (!legacyToken) {
    return;
  }

  sessionStorage.setItem(tokenKey, legacyToken);
  localStorage.removeItem(tokenKey);
  inMemoryAccessToken = legacyToken;
};

const migrateLegacyRefreshTokenIfNeeded = (): void => {
  const sessionStorage = getSessionStorage();
  const localStorage = getLocalStorage();

  if (!sessionStorage || !localStorage) {
    return;
  }

  if (sessionStorage.getItem(refreshTokenKey)) {
    return;
  }

  const legacyRefreshToken = localStorage.getItem(refreshTokenKey);
  if (!legacyRefreshToken) {
    return;
  }

  sessionStorage.setItem(refreshTokenKey, legacyRefreshToken);
  localStorage.removeItem(refreshTokenKey);
};

const migrateLegacyUserInfoIfNeeded = (): void => {
  const sessionStorage = getSessionStorage();
  const localStorage = getLocalStorage();

  if (!sessionStorage || !localStorage) {
    return;
  }

  if (sessionStorage.getItem(userInfoKey)) {
    localStorage.removeItem(userInfoKey);
    return;
  }

  const legacyUserInfo = localStorage.getItem(userInfoKey);
  if (!legacyUserInfo) {
    return;
  }

  sessionStorage.setItem(userInfoKey, legacyUserInfo);
  localStorage.removeItem(userInfoKey);
};

const readTabUserInfo = (): UserInfo | null => {
  const value = getSessionStorage()?.getItem(userInfoKey);
  if (!value) return null;
  try {
    const info: unknown = JSON.parse(value);
    if (!info || typeof info !== "object" || !("id" in info)) return null;
    return info as UserInfo;
  } catch {
    return null;
  }
};

export const localAuthRepository = {
  getStaffSessionRevision(): string {
    return JSON.stringify([
      getLocalStorage()?.getItem(staffSessionRevisionKey) ?? null,
      getSessionStorage()?.getItem(staffTabRevisionKey) ?? null,
    ]);
  },
  isStaffSessionCurrent(): boolean {
    const globalRevision =
      getLocalStorage()?.getItem(staffSessionRevisionKey) ?? null;
    const tabRevision =
      getSessionStorage()?.getItem(staffTabRevisionKey) ?? null;
    // A fresh tab with no old identity may join the current Cookie session.
    if (
      !tabRevision &&
      !this.getToken() &&
      !getSessionStorage()?.getItem(userInfoKey)
    )
      return true;
    return globalRevision === tabRevision;
  },
  setStaffRefreshedToken(token: string, expectedRevision: string): boolean {
    if (
      this.getStaffSessionRevision() !== expectedRevision ||
      !this.isStaffSessionCurrent()
    )
      return false;
    const shared = getLocalStorage()?.getItem(staffSessionRevisionKey);
    if (shared) getSessionStorage()?.setItem(staffTabRevisionKey, shared);
    inMemoryAccessToken = token;
    getSessionStorage()?.setItem(tokenKey, token);
    getLocalStorage()?.removeItem(tokenKey);
    return true;
  },
  member: memberSessionStorage,
  // 復帰時の照合にだけ使用する。古いユーザー情報を画面・通常APIへ返さない。
  getStaffRecoveryIdentity(): UserInfo | null {
    const info = readTabUserInfo();
    const id: unknown = info?.id;
    return info &&
      typeof id === "string" &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        id
      ) &&
      !isLegacyMemberToken(this.getToken())
      ? info
      : null;
  },
  invalidateStaffTab(): void {
    // Cookieとの不一致をこのタブだけで扱い、他タブの世代は変更しない。
    getSessionStorage()?.setItem(staffTabRevisionKey, crypto.randomUUID());
    if (typeof window !== "undefined")
      window.dispatchEvent(new Event("authStateChanged"));
  },
  restoreStaffSession(
    token: string,
    userInfo: UserInfo,
    expectedRevision: string
  ): boolean {
    const oldIdentity = this.getStaffRecoveryIdentity();
    if (
      this.getStaffSessionRevision() !== expectedRevision ||
      !oldIdentity ||
      !userInfo ||
      typeof userInfo !== "object" ||
      userInfo.id !== oldIdentity.id ||
      userInfo.organizationId !== oldIdentity.organizationId ||
      !token ||
      isLegacyMemberToken(token)
    )
      return false;
    const shared = getLocalStorage()?.getItem(staffSessionRevisionKey);
    if (!shared) return false;
    // 一致が確定してから、同じ世代のtoken/profileをまとめて公開する。
    getSessionStorage()?.setItem(staffTabRevisionKey, shared);
    inMemoryAccessToken = token;
    getSessionStorage()?.setItem(tokenKey, token);
    this.setUserInfo(userInfo);
    return true;
  },
  getLegacyMemberCredentials() {
    const accessToken = this.getToken();
    if (!isLegacyMemberToken(accessToken)) return null;
    const refreshToken = this.getRefreshToken();
    return accessToken && refreshToken ? { accessToken, refreshToken } : null;
  },
  discardLegacyMemberCredentials(expected: {
    accessToken: string;
    refreshToken: string;
  }) {
    if (
      this.getToken() !== expected.accessToken ||
      this.getRefreshToken() !== expected.refreshToken
    )
      return;
    this.deleteToken();
    this.deleteRefreshToken();
    this.deleteUserInfo();
  },
  getStaffToken(): string | null {
    if (!this.isStaffSessionCurrent()) return null;
    const token = this.getToken();
    return isLegacyMemberToken(token) ? null : token;
  },
  // --- アクセストークン ---
  setToken(token: string): void {
    if (!isLegacyMemberToken(token)) {
      const revision = crypto.randomUUID();
      getLocalStorage()?.setItem(staffSessionRevisionKey, revision);
      getSessionStorage()?.setItem(staffTabRevisionKey, revision);
    }
    inMemoryAccessToken = token;
    getSessionStorage()?.setItem(tokenKey, token);
    getLocalStorage()?.removeItem(tokenKey);
  },
  getToken(): string | null {
    if (inMemoryAccessToken) {
      return inMemoryAccessToken;
    }

    migrateLegacyTokenIfNeeded();

    const tokenFromSession = getSessionStorage()?.getItem(tokenKey) ?? null;
    inMemoryAccessToken = tokenFromSession;

    return inMemoryAccessToken;
  },
  deleteToken(): void {
    inMemoryAccessToken = null;
    getSessionStorage()?.removeItem(tokenKey);
    getLocalStorage()?.removeItem(tokenKey);
  },

  // --- リフレッシュトークン ---
  setRefreshToken(token: string): void {
    getSessionStorage()?.setItem(refreshTokenKey, token);
    getLocalStorage()?.removeItem(refreshTokenKey);
  },
  getRefreshToken(): string | null {
    migrateLegacyRefreshTokenIfNeeded();
    return getSessionStorage()?.getItem(refreshTokenKey) ?? null;
  },
  deleteRefreshToken(): void {
    getSessionStorage()?.removeItem(refreshTokenKey);
    getLocalStorage()?.removeItem(refreshTokenKey);
  },

  // --- 信頼済みデバイストークン ---
  // 2FA 成功時（このデバイスを記憶）に発行され、次回ログイン時に送ると 2FA を
  // スキップできる長期トークン。30日間有効なためセッションをまたいで保持する。
  setTrustedDeviceToken(token: string): void {
    getLocalStorage()?.setItem(trustedDeviceTokenKey, token);
  },
  getTrustedDeviceToken(): string | null {
    return getLocalStorage()?.getItem(trustedDeviceTokenKey) ?? null;
  },
  deleteTrustedDeviceToken(): void {
    getLocalStorage()?.removeItem(trustedDeviceTokenKey);
  },

  // --- ユーザー情報 ---
  setUserInfo(userInfo: UserInfo): void {
    // access token と同じタブ単位で保持する。
    // localStorage に置くと、期限切れ状態が異なる複数タブが storage event を
    // 互いに発火し、/me の再取得とセッション破棄を繰り返す原因になる。
    getSessionStorage()?.setItem(userInfoKey, JSON.stringify(userInfo));
    getLocalStorage()?.removeItem(userInfoKey);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("authStateChanged"));
    }
  },
  getUserInfo(): UserInfo | null {
    if (!this.isStaffSessionCurrent()) return null;
    migrateLegacyUserInfoIfNeeded();
    return readTabUserInfo();
  },
  deleteUserInfo(): void {
    getSessionStorage()?.removeItem(userInfoKey);
    getLocalStorage()?.removeItem(userInfoKey);
  },

  clearAll(): void {
    // A stale tab must not log out a newer login in another tab.
    const revision = crypto.randomUUID();
    if (this.isStaffSessionCurrent())
      getLocalStorage()?.setItem(staffSessionRevisionKey, revision);
    getSessionStorage()?.setItem(staffTabRevisionKey, revision);
    this.deleteToken();
    this.deleteRefreshToken();
    this.deleteUserInfo();
  },
};
